<template>
  <div v-if="schedule" ref="rootElem" class="schedule" :style="scheduleStyle">
    <div class="row row-sticky" :class="{ extended: averageActive }" :style="rowGridStyle">
      <div
        v-for="(day, dayIndex) in schedule.days"
        :key="dayIndex"
        :ref="el => registerDayCol(dayIndex, el)"
        class="day-header col"
        @mouseenter="setActiveDay(dayIndex)"
        @mouseleave="setActiveDay()"
      >
        <Tooltip placement="bottom">
          <template #trigger>
            <div class="day-norm"><div class="day-norm__icon" :class="day.normClass" /></div>
          </template>
          <div>
            <div class="day-norm__title">Odchylenia</div>
            <div class="day-norm__deviations">
              <div class="day-norm__box day-norm__box--ok" />
              0 - 10 %
            </div>
            <div class="day-norm__deviations">
              <div class="day-norm__box day-norm__box--yellow" />
              10 - 20 %
            </div>
            <div class="day-norm__deviations">
              <div class="day-norm__box day-norm__box--red" />
              > 20 %
            </div>
          </div>
        </Tooltip>
        <Tooltip placement="top">
          <template #trigger>
            <div class="day-link">
              <router-link
                :to="{
                  name: 'DietDetailDay',
                  params: {
                    dietType: $route.params.dietType,
                    dietId: $route.params.dietId,
                    dayIndex: getAbsoluteDayIndex(dayIndex).toString()
                  }
                }"
              >
                <span>
                  {{ getScheduleDayHeader(getAbsoluteDayIndex(dayIndex), diet?.data.start_date).name }}
                </span>
                <span
                  v-if="getScheduleDayHeader(getAbsoluteDayIndex(dayIndex), diet?.data.start_date).date"
                  class="day-link__date"
                >
                  {{ getScheduleDayHeader(getAbsoluteDayIndex(dayIndex), diet?.data.start_date).date }}
                </span>
              </router-link>
            </div>
          </template>
          <div>Przejdź do widoku dziennego</div>
        </Tooltip>
        <div class="day-menu">
          <IconButton name="more_vert" @click.stop="setActiveMenu(`day-menu-${dayIndex}`)" />
          <div
            v-if="checkActiveMenu(`day-menu-${dayIndex}`)"
            class="day-menu__items"
            :class="{ reversed: isLastVisibleDay(dayIndex) && !averageActive }"
          >
            <div class="day-menu__item" @click.once="copyDay(getAbsoluteDayIndex(dayIndex))">
              <BaseIcon size="16" name="filter_none" />
              <span>Skopiuj dzień</span>
            </div>
            <div
              v-if="canDietBeEdited"
              class="day-menu__item"
              :class="{ disabled: !copiedDayStatus }"
              @click.once="pasteDay(getAbsoluteDayIndex(dayIndex))"
            >
              <BaseIcon size="16" name="queue" />
              <span>Wklej dzień</span>
            </div>
            <div
              v-if="canDietBeEdited"
              class="day-menu__item"
              @click.once="setActiveDayDeleteModal(getAbsoluteDayIndex(dayIndex))"
            >
              <BaseIcon size="16" name="delete" />
              <span>Wyczyść dzień</span>
            </div>
          </div>
          <BaseModal
            v-if="activeDayDeleteModal === getAbsoluteDayIndex(dayIndex)"
            @close="setActiveDayDeleteModal()"
          >
            <template #title>Czy na pewno chcesz wyczyścić dzień?</template>
            Wyczyszczenie dnia usunie aktualnie dodane potrawy w kolumnie
            <span>
              {{ getDayDisplayName(getAbsoluteDayIndex(dayIndex), diet?.data.diet_type, diet?.data.start_date) }}
            </span>
            .
            <template #actions>
              <StyledButton variant="outline" @click.once="setActiveDayDeleteModal()">
                Anuluj
              </StyledButton>
              <StyledButton
                @click.once="setActiveDayDeleteModal(), deleteDay(getAbsoluteDayIndex(dayIndex))"
              >
                Wyczyść
              </StyledButton>
            </template>
          </BaseModal>
        </div>
      </div>
      <div class="average-header">
        <Tooltip placement="top-end">
          <template #trigger>
            <div class="average-button">
              <IconButton
                :name="averageActive ? 'chevron_right' : 'chevron_left'"
                icon-size="24"
                @click="handleToggleAverageActive"
              />
            </div>
          </template>
          <div>{{ averageActive ? 'Ukryj podsumowanie' : 'Pokaż podsumowanie' }}</div>
        </Tooltip>
        <div class="average-title">Średnia</div>
      </div>
    </div>
    <div
      v-for="(meal, mealIndex) in schedule.meals"
      :key="`meal-${mealIndex}`"
      class="meal row"
      :class="{ extended: averageActive }"
      :style="rowGridStyle"
    >
      <div class="meal-name"><span :style="centeredLabelStyle">{{ meal.name }}</span></div>
      <div
        v-for="(day, dayIndex) in meal.days"
        :key="`meal-${mealIndex}-day-${dayIndex}`"
        :ref="el => registerDayCol(dayIndex, el)"
        class="day col"
        :data-day="`day-${dayIndex}`"
        @mouseenter="setActiveDay(dayIndex)"
        @mouseleave="setActiveDay()"
        @drop="
          canDietBeEdited
            ? dropDishOrIngredientHandler($event, mealIndex, getAbsoluteDayIndex(dayIndex))
            : ''
        "
        @dragover.prevent
      >
        <div class="energy-distribution-hour">
          <div
            class="day-hour base-select"
            :class="{
              'is-disabled': !canDietBeEdited
            }"
          >
            <div
              class="base-select__selected"
              :class="{
                'base-select__selected--active': checkActiveMenu(
                  `day-hour-${mealIndex}-${dayIndex}`
                )
              }"
              @click.stop="() => {
                setActiveMenu(`day-hour-${mealIndex}-${dayIndex}`, true)
                pushDataLayer({
                  event: 'diet_timepicker_click'
                })
              }"
            >
              {{ day.hour }}
            </div>
            <div
              v-if="checkActiveMenu(`day-hour-${mealIndex}-${dayIndex}`)"
              class="base-select__options"
              :class="[`day-hour-${mealIndex}-${dayIndex}-options`]"
            >
              <div
                v-for="dayHour in hours"
                :key="dayHour"
                class="base-select__option"
                :class="{ 'base-select__option--selected': dayHour === day.hour }"
                @click="updateDayHour(mealIndex, getAbsoluteDayIndex(dayIndex), dayHour)"
              >
                {{ dayHour }}
              </div>
            </div>
          </div>
          <Tooltip
            v-if="schedule.energyDistribution && day.dishesAndIngredients.length > 0"
            placement="top-end"
          >
            <template #trigger>
              <div class="energy-distribution">
                <div class="energy-distribution__dot" :class="day.energyDistributionClass" />
              </div>
            </template>
            <div>
              <div class="energy-distribution__title">Rozkład energii</div>
              <div class="energy-distribution__data">
                {{ day.energyDistribution }}
                %
                <span class="energy-distribution__data-target">
                  ({{ meal.energyDistribution }}
                  %)
                </span>
                -{{ day.calories }}
                kcal
                <span class="energy-distribution__data-target">
                  ({{ meal.energyDistributionCalories }}
                  kcal)
                </span>
              </div>
            </div>
          </Tooltip>
        </div>
        <div
          v-for="(dishOrIngredient, dishOrIngredientIndex) in day.dishesAndIngredients"
          :key="`dish-or-ingredient-${mealIndex}-${dayIndex}-${dishOrIngredientIndex}`"
          class="dish-or-ingredient"
          :draggable="true ? canDietBeEdited : false"
          :class="{ repeated: dishOrIngredient.repeated }"
          @click="
            redirectToDietDetailDay(mealIndex, getAbsoluteDayIndex(dayIndex), dishOrIngredientIndex)
          "
          @mousedown.ctrl="updateDragMode('copy')"
          @dragstart="
            dragStartDishOrIngredientHandler(
              $event,
              mealIndex,
              getAbsoluteDayIndex(dayIndex),
              dishOrIngredientIndex
            )
          "
        >
          <Tooltip v-if="hasPerm('accounts.diet_btw_bar')" placement="top">
            <template #trigger>
              <div class="nutrients-bar">
                <div class="nutrients-bar__content">
                  <div
                    class="nutrients-bar__bar nutrients-bar__protein"
                    :style="
                      nutrientBarWidthStyle(
                        dishOrIngredient.calories,
                        dishOrIngredient.proteinPercent
                      )
                    "
                  >
                    <span
                      :class="
                        nutrientBarHideClass(
                          dishOrIngredient.calories,
                          dishOrIngredient.proteinPercent
                        )
                      "
                    >
                      B
                    </span>
                  </div>
                  <div
                    class="nutrients-bar__bar nutrients-bar__fat"
                    :style="
                      nutrientBarWidthStyle(
                        dishOrIngredient.calories,
                        dishOrIngredient.fatPercent
                      )
                    "
                  >
                    <span
                      :class="
                        nutrientBarHideClass(
                          dishOrIngredient.calories,
                          dishOrIngredient.fatPercent
                        )
                      "
                    >
                      T
                    </span>
                  </div>
                  <div
                    class="nutrients-bar__bar nutrients-bar__carbohydrates"
                    :style="
                      nutrientBarWidthStyle(
                        dishOrIngredient.calories,
                        dishOrIngredient.carbohydratesPercentBar
                      )
                    "
                  >
                    <span
                      :class="
                        nutrientBarHideClass(
                          dishOrIngredient.calories,
                          dishOrIngredient.carbohydratesPercentBar
                        )
                      "
                    >
                      W
                    </span>
                  </div>
                </div>
              </div>
            </template>
            <div>
              <div>
                <div class="nutrients-bar__box nutrients-bar__protein" />
                {{ dishOrIngredient.proteinPercent }}
                % - Białko
              </div>
              <div>
                <div class="nutrients-bar__box nutrients-bar__fat" />
                {{ dishOrIngredient.fatPercent }}
                % - Tłuszcze
              </div>
              <div>
                <div class="nutrients-bar__box nutrients-bar__carbohydrates" />
                {{ dishOrIngredient.carbohydratesPercent }}
                % - Węglowodany
              </div>
            </div>
          </Tooltip>
          <div class="dish-or-ingredient__name">{{ dishOrIngredient.name }}</div>
          <div class="dish-or-ingredient__footer">
            <div class="dish-or-ingredient__calories">{{ dishOrIngredient.calories }} kcal</div>
            <div v-if="canDietBeEdited" class="dish-or-ingredient__actions">
              <Tooltip
                v-if="repeatedDishesAndIngredientsActive && dishOrIngredient.repeated"
                :placement="isLastVisibleDay(dayIndex) ? 'top-end' : 'top'"
              >
                <template #trigger>
                  <IconButton
                    size="small"
                    icon-size="16"
                    name="assignment"
                    :type="dishOrIngredient.repeated ? 'warning' : 'default'"
                    @click.stop="
                      showRepeatedDishDietsModal(dishOrIngredient.name, dishOrIngredient.type)
                    "
                  />
                </template>
                <div>
                  Wyświetl
                  <span v-if="dishOrIngredient.type == 'dish'">powtarzającą się potrawę</span>
                  <span v-else>powtarzający się produkt</span>
                </div>
              </Tooltip>
              <Tooltip :placement="isLastVisibleDay(dayIndex) ? 'top-end' : 'top'">
                <template #trigger>
                  <IconButton
                    name="filter_none"
                    size="small"
                    icon-size="14"
                    :type="dishOrIngredient.repeated ? 'warning' : 'default'"
                    @click.stop="
                      copyDishOrIngredientHandler(
                        mealIndex,
                        getAbsoluteDayIndex(dayIndex),
                        dishOrIngredientIndex
                      )
                    "
                    @mousedown="updateDragMode('copy')"
                  />
                </template>
                <div>W celu skopiowania kliknij w ikonę lub przeciągnij posiłek</div>
              </Tooltip>
              <Tooltip :placement="isLastVisibleDay(dayIndex) ? 'top-end' : 'top'">
                <template #trigger>
                  <IconButton
                    name="delete"
                    hover-type="destructive"
                    :type="dishOrIngredient.repeated ? 'warning' : 'default'"
                    size="small"
                    icon-size="16"
                    @click.stop="
                      deleteDishOrIngredient(
                        mealIndex,
                        getAbsoluteDayIndex(dayIndex),
                        dishOrIngredientIndex
                      )
                    "
                  />
                </template>
                <div>W celu usunięcia posiłku kliknij w ikonę</div>
              </Tooltip>
            </div>
          </div>
        </div>
        <div v-if="canDietBeEdited" class="add-dish-or-ingredient">
          <StyledButton
            v-if="
              !checkActiveAddDishOrIngredient(`add-dish-or-ingredient-${mealIndex}-${dayIndex}`)
            "
            class="button"
            variant="outline"
            left-icon="add"
            @click.stop="
              setActiveAddDishOrIngredient(`add-dish-or-ingredient-${mealIndex}-${dayIndex}`)
            "
            @keyup.enter="
              setActiveAddDishOrIngredient(`add-dish-or-ingredient-${mealIndex}-${dayIndex}`)
            "
          >
            Dodaj pozycję
          </StyledButton>
          <DishOrIngredientAddInput
            v-else
            test-id="DietDetailWeekSchedule"
            mode="all"
            :dish-meal-type="meal.type"
            :filters="dishFlags"
            :related-dishes="relatedDishes"
            @select="
              addDishOrIngredient(
                $event.dishOrIngredientId,
                $event.dishOrIngredientType,
                mealIndex,
                getAbsoluteDayIndex(dayIndex)
              )
            "
            @select-unused="
              addUnusedDishPortion(
                $event.mealIndex,
                $event.dayIndex,
                $event.dishOrIngredientIndex,
                mealIndex,
                getAbsoluteDayIndex(dayIndex)
              )
            "
            @keyup.esc="setActiveAddDishOrIngredient()"
            @close="setActiveAddDishOrIngredient()"
          />
        </div>
      </div>
      <div class="meal-average" :class="{ inactive: !averageActive }">
        <template v-if="averageActive && meal.mealExists">
          <div class="meal-average__kcal">
            <span>{{ meal.average.calories }}</span>
            <span v-if="energyDistributionEnabled" class="meal-target__kcal">
              /{{ meal.energyDistributionCalories }}
            </span>
            <span :class="{ regular: energyDistributionEnabled }">kcal</span>
          </div>
          <div v-if="energyDistributionEnabled" class="meal-average__percent">
            <span>{{ meal.actualEnergyDistribution }}</span>
            <span class="meal-target__percent">/{{ meal.energyDistribution }}</span>
            %
          </div>
          <div class="meal-average__nutrients">
            <div class="meal-average__nutrient">
              <div class="meal-average__nutrient-name">Białko</div>
              <div class="meal-average__nutrient-value">{{ meal.average.protein }}g</div>
              <div class="meal-average__nutrient-percent">{{ meal.average.proteinPercent }}%</div>
            </div>
            <div class="meal-average__nutrient">
              <div class="meal-average__nutrient-name">Tłuszcze</div>
              <div class="meal-average__nutrient-value">{{ meal.average.fat }}g</div>
              <div class="meal-average__nutrient-percent">{{ meal.average.fatPercent }}%</div>
            </div>
            <div class="meal-average__nutrient">
              <div class="meal-average__nutrient-name">Wp</div>
              <div class="meal-average__nutrient-value">{{ meal.average.carbohydrates }}g</div>
              <div class="meal-average__nutrient-percent">
                {{ meal.average.carbohydratesPercent }}
                %
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <RepeatedDishDietsModal
      v-if="repeatedDishDietsModal && repeatedDishDataType === 'dish' && diet"
      :current-diet-active="diet.data.repeated_current_diet_active ?? true"
      :dish-name="repeatedDishDataName"
      :meals="diet.data.meals"
      :previous-diets-active="diet.data.repeated_previous_diets_active ?? true"
      :repeated-diets="repeatedDishDietsData ?? []"
      @close="hideRepeatedDishDietsModal"
    />
    <BaseModal v-else-if="repeatedDishDietsModal" @close="hideRepeatedDishDietsModal">
      <template #title>
        <span v-if="repeatedDishDataType == 'dish'">Powtarzająca się potrawa</span>
        <span v-else>Powtarzający się produkt</span>
      </template>
      <div class="doi-name">{{ repeatedDishDataName }}</div>
      <div class="doi-description">
        <span v-if="repeatedDishDataType == 'dish'">Ta potrawa</span>
        <span v-else>Ten produkt</span>
        występuje w następujących jadłospisach pacjenta (opcja uwzględnia do 5 jadłospisów wstecz):
      </div>
      <div
        v-for="repeatedDiet in repeatedDishDietsData"
        :key="`diet-${repeatedDiet.id}`"
        class="repeated-dish-diets-row"
      >
        <a :href="repeatedDiet.url" target="_blank">
          {{ repeatedDiet.name }}
          -{{ repeatedDiet.start_date }}
          -{{ repeatedDiet.end_date }}
          (liczba wystąpień:
          {{ repeatedDiet.number_of_occurences }}
          )
        </a>
      </div>
    </BaseModal>
  </div>
</template>

<script lang="ts" setup>
  import { A, G } from '@mobily/ts-belt'
  import { useEventListener } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, nextTick, onBeforeUpdate, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import BaseIcon from '@/components/BaseIcon.vue'
  import BaseModal from '@/components/BaseModal.vue'
  import DishOrIngredientAddInput from '@/components/DishOrIngredientAddInput.vue'
  import RepeatedDishDietsModal from '@/components/diets/RepeatedDishDietsModal.vue'
  import { IconButton } from '@/components/IconButton'
  import { StyledButton } from '@/components/StyledButton'
  import { Tooltip } from '@/components/ui'
  import {
    DIET_AVERAGE_COLUMN_EXPANDED_WIDTH_PX,
    DIET_AVERAGE_COLUMN_WIDTH_PX,
    DIET_WEEK_DAYS_COUNT,
    getDayDisplayName,
    getScheduleDayHeader,
    hours,
  } from '@/const'
  import { dietsService } from '@/services/dietsService'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { pushDataLayer } from '@/utils/pushDataLayer'
  import { reportError } from '@/utils/reportError'

  import type { RepeatedUsedDiet } from '@/services/dietsService'
  import type { Schedule } from '@/types/Diet'

  const rootElem = ref<HTMLDivElement>()
  const activeMenu = ref<string>()
  const props = defineProps<{
    schedule: Schedule
    startDayIndex: number
  }>()

  const refs = ref<Record<number, HTMLDivElement[]>>({})
  const activeDayDeleteModal = ref<number>()
  const activeAddDishOrIngredient = ref<string>()
  const repeatedDishDietsModal = ref(false)
  const repeatedDishDietsData = ref<RepeatedUsedDiet[]>()
  const repeatedDishDataName = ref('')
  const repeatedDishDataType = ref('')

  const $router = useRouter()
  const $route = useRoute()
  const dietStore = useDietsStore()
  const globalStore = useGlobalStore()
  const {
    averageActive,
    copiedDayStatus,
    dragMode,
    relatedDishes,
    diet,
    repeatedDishesAndIngredientsActive,
    canDietBeEdited,
  } = storeToRefs(dietStore)

  const { hasPerm } = storeToRefs(globalStore)

  const {
    updateDragMode,
    copyDay,
    pasteDay,
    deleteDay,
    updateDayHour,
    fetchAndAddDishOrIngredient,
    moveDishOrIngredient,
    copyDishOrIngredient,
    deleteDishOrIngredient,
    toggleAverageActive,
  } = dietStore

  const dishFlags = computed(() => diet.value?.data.dish_search_filters)
  const energyDistributionEnabled = computed(() => diet.value?.data.energy_distribution ?? false)
  const getAbsoluteDayIndex = (dayIndex: number) => props.startDayIndex + dayIndex
  const visibleDaysCount = computed(() => props.schedule.days.length)
  const averageColumnWidthPx = computed(() =>
    averageActive.value ? DIET_AVERAGE_COLUMN_EXPANDED_WIDTH_PX : DIET_AVERAGE_COLUMN_WIDTH_PX,
  )
  const rowGridStyle = computed(() => {
    const dayColumns =
      visibleDaysCount.value <= DIET_WEEK_DAYS_COUNT
        ? `repeat(${visibleDaysCount.value}, calc(${100 / DIET_WEEK_DAYS_COUNT}% - ${
            averageColumnWidthPx.value / DIET_WEEK_DAYS_COUNT
          }px)) minmax(0, 1fr)`
        : `repeat(${visibleDaysCount.value}, minmax(0, 1fr))`

    return {
      gridTemplateColumns: `${dayColumns} [average] ${averageColumnWidthPx.value}px`,
    }
  })
  const scheduleStyle = computed(() => {
    // Parent width (diet-detail) is sized for day columns only when all days are shown.
    // Keep the average column additive so opening it does not shrink every day column.
    if (visibleDaysCount.value > DIET_WEEK_DAYS_COUNT) {
      return { width: `calc(100% + ${averageColumnWidthPx.value}px)` }
    }

    return { width: '100%' }
  })
  const centeredLabelStyle = computed(() => {
    if (visibleDaysCount.value <= DIET_WEEK_DAYS_COUNT) {
      return { width: '100%' }
    }

    return {
      width: `calc((100% - ${averageColumnWidthPx.value}px) * ${
        DIET_WEEK_DAYS_COUNT / visibleDaysCount.value
      })`,
    }
  })
  const isLastVisibleDay = (dayIndex: number) => dayIndex === visibleDaysCount.value - 1

  const registerDayCol = (dayIndex: number, el: unknown) => {
    if (el instanceof HTMLDivElement) {
      refs.value[dayIndex] ??= []
      refs.value[dayIndex].push(el)
    }
  }

  const showRepeatedDishDietsModal = async (doiName: string, doiType: 'dish' | 'ingredient') => {
    try {
      const response = await dietsService.fetchPatientDietRepeatedDishDiets(
        parseInt($route.params.dietId as string),
        doiName,
        diet.value?.data.repeated_diets_count ?? 5,
      )

      repeatedDishDietsModal.value = true
      repeatedDishDietsData.value = response.data
      repeatedDishDataName.value = doiName
      repeatedDishDataType.value = doiType
    } catch (err) {
      reportError(err, 'Error while fetching repeated dish diets data', {
        dietId: $route.params.dietId,
        doiName,
        doiType,
      })
    }
  }
  const hideRepeatedDishDietsModal = () => {
    repeatedDishDietsModal.value = false
    repeatedDishDietsData.value = undefined
    repeatedDishDataName.value = ''
    repeatedDishDataType.value = ''
  }
  const setActiveDay = (dayIndex?: number) => {
    activeMenu.value = undefined
    setTimeout(() => {
      document.querySelectorAll('.col').forEach(col => {
        col.classList.remove('active')
      })
      if (G.isNotNullable(dayIndex)) {
        A.forEach(refs.value[dayIndex] ?? [], col => {
          col.classList.add('active')
        })
      }
    }, 0)
  }
  const setActiveMenu = async (menu?: string, hourMenu = false) => {
    activeAddDishOrIngredient.value = undefined
    if (activeMenu.value === menu || !menu) {
      activeMenu.value = undefined
    } else {
      activeMenu.value = menu
      if (hourMenu) {
        await nextTick()

        const activeOptions = rootElem.value?.querySelector(`.${activeMenu.value}-options`)
        const activeElement = rootElem.value?.querySelector('.base-select__option--selected')

        if (activeOptions instanceof HTMLElement && activeElement instanceof HTMLElement) {
          activeOptions.scrollTo(0, activeElement.offsetTop - activeOptions.offsetHeight / 2)
        }
      }
    }
  }
  const checkActiveMenu = (menu: string) => {
    return activeMenu.value === menu
  }
  const setActiveAddDishOrIngredient = (dishOrIngredient?: string) => {
    activeMenu.value = undefined
    activeAddDishOrIngredient.value = dishOrIngredient
  }
  const checkActiveAddDishOrIngredient = (dishOrIngredient: string) => {
    return activeAddDishOrIngredient.value === dishOrIngredient
  }
  const setActiveDayDeleteModal = (dayIndex?: number) => {
    activeDayDeleteModal.value = dayIndex
  }

  const addDishOrIngredient = async (
    dishOrIngredientId: number,
    dishOrIngredientType: 'dish' | 'ingredient',
    mealIndex: number,
    dayIndex: number,
  ) => {
    try {
      await fetchAndAddDishOrIngredient({
        dayIndex,
        id: dishOrIngredientId,
        mealIndex,
        type: dishOrIngredientType,
      })

      setActiveAddDishOrIngredient()
    } catch (err) {
      reportError(err, 'Error during fetching and adding dish or ingredient', {
        dayIndex,
        id: dishOrIngredientId,
        mealIndex,
        type: dishOrIngredientType,
      })
    }
  }
  const addUnusedDishPortion = (
    mealIndex: number,
    dayIndex: number,
    dishOrIngredientIndex: number,
    targetMealIndex: number,
    targetDayIndex: number,
  ) => {
    copyDishOrIngredient(
      targetMealIndex,
      targetDayIndex,
      mealIndex,
      dayIndex,
      dishOrIngredientIndex,
      true,
    )
  }
  const dragStartDishOrIngredientHandler = (
    event: DragEvent,
    mealIndex: number,
    dayIndex: number,
    dishOrIngredientIndex: number,
  ) => {
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copyMove'
      event.dataTransfer.dropEffect = dragMode.value as DataTransfer['dropEffect']
      event.dataTransfer.setData('mealIndex', mealIndex.toString())
      event.dataTransfer.setData('dayIndex', dayIndex.toString())
      event.dataTransfer.setData('dishOrIngredientIndex', dishOrIngredientIndex.toString())
    }
  }
  const copyDishOrIngredientHandler = (
    mealIndex: number,
    dayIndex: number,
    dishOrIngredientIndex: number,
  ) => {
    copyDishOrIngredient(mealIndex, dayIndex, mealIndex, dayIndex, dishOrIngredientIndex)
    updateDragMode('move')
  }
  const dropDishOrIngredientHandler = (event: DragEvent, mealIndex: number, dayIndex: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      const eventMealIndex = parseInt(event.dataTransfer.getData('mealIndex'))
      const eventDayIndex = parseInt(event.dataTransfer.getData('dayIndex'))
      const eventDishOrIngredientIndex = parseInt(
        event.dataTransfer.getData('dishOrIngredientIndex'),
      )
      if (
        Number.isNaN(eventMealIndex) ||
        Number.isNaN(eventDayIndex) ||
        Number.isNaN(eventDishOrIngredientIndex)
      ) {
        reportError(new Error('Invalid data transfer data'), 'Invalid data transfer data', {
          eventDayIndex,
          eventDishOrIngredientIndex,
          eventMealIndex,
        })
        return
      }
      if (dragMode.value === 'copy') {
        copyDishOrIngredient(
          mealIndex,
          dayIndex,
          eventMealIndex,
          eventDayIndex,
          eventDishOrIngredientIndex,
        )
      } else {
        moveDishOrIngredient(
          mealIndex,
          dayIndex,
          eventMealIndex,
          eventDayIndex,
          eventDishOrIngredientIndex,
        )
      }
    }
    updateDragMode('move')
  }

  const redirectToDietDetailDay = async (
    mealIndex: number,
    dayIndex: number,
    dishOrIngredientIndex: number,
  ) => {
    sessionStorage.setItem('redirectMealIndex', mealIndex.toString())
    sessionStorage.setItem('redirectDishOrIngredientIndex', dishOrIngredientIndex.toString())
    try {
      await $router.push({
        name: 'DietDetailDay',
        params: {
          dayIndex,
          dietId: $route.params.dietId,
          dietType: $route.params.dietType,
        },
      })
    } catch (err) {
      reportError(err, 'Error while redirecting to diet detail day', {
        dayIndex,
        dishOrIngredientIndex,
        mealIndex,
      })
    }
  }

  const nutrientBarWidthStyle = (calories: number, percent: number) => {
    if (calories) return { width: `${percent}%` }
    return {}
  }
  const nutrientBarHideClass = (calories: number, percent: number) => {
    if (calories && percent < 10) return 'hide'
    return {}
  }
  const hideMenu = () => {
    activeMenu.value = undefined
    activeAddDishOrIngredient.value = undefined
  }

  const getHorizontalScrollParent = (element: HTMLElement) => {
    let parent = element.parentElement
    while (parent) {
      const { overflowX } = getComputedStyle(parent)
      if (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') {
        return parent
      }
      parent = parent.parentElement
    }
    return null
  }

  const handleToggleAverageActive = async () => {
    const willOpen = !averageActive.value
    toggleAverageActive()
    if (!willOpen || visibleDaysCount.value <= DIET_WEEK_DAYS_COUNT || !rootElem.value) return

    await nextTick()
    requestAnimationFrame(() => {
      const root = rootElem.value
      if (!root) return

      const scrollParent = getHorizontalScrollParent(root)
      if (!scrollParent) return

      scrollParent.scrollTo({
        behavior: 'smooth',
        left: scrollParent.scrollWidth,
      })
    })
  }

  useEventListener('click', hideMenu)

  onBeforeUpdate(() => {
    A.forEach(
      Array.from({ length: visibleDaysCount.value }, (_, dayIndex) => dayIndex),
      dayIndex => {
        refs.value[dayIndex] = []
      },
    )
  })
</script>

<style scoped>
  .row {
    display: grid;
    min-width: 0;
  }

  .schedule {
    position: relative;
    box-sizing: border-box;
    min-width: 0;
    max-width: none;
  }

  .col {
    min-width: 0;
  }

  .row-sticky {
    position: sticky;

    /* Sit below DietDetailScheduleToolbar (padding 12+12 + controls ~34) */
    top: 58px;
    z-index: 11;
    background: #fff;
  }

  /* Day header */
  .day-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 46px;
  }

  .day-header.active::after {
    position: absolute;
    inset: 0;
    pointer-events: none;
    content: "";
    box-shadow: 0 4px 5px 3px rgb(222 222 222 / 50%);
    clip-path: inset(0 -15px 0 -15px);
  }

  .repeated-dish-diets-row a {
    display: flex;
    margin-top: 10px;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-main-100);
    text-decoration: none;
  }

  .day-link a {
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: Montserrat-Medium;
    font-size: 12px;
    line-height: 16px;
    color: var(--color-primary-text);
    text-decoration: none;
  }

  .day-link__date {
    color: var(--color-secondary-text);
  }

  .repeated-dish-diets-row a:hover {
    color: var(--color-main-110);
    text-decoration: underline;
  }

  /* Day */
  .day {
    position: relative;
    box-sizing: border-box;
    min-width: 0;
    padding: 32px 6px 8px;
  }

  .day.active::after {
    position: absolute;
    inset: 0;
    pointer-events: none;
    content: "";
    box-shadow: 0 4px 5px 3px rgb(222 222 222 / 50%);
    clip-path: inset(0 -15px 0 -15px);
  }

  /* Day norm */
  .day-norm {
    position: absolute;
    top: 14px;
    left: 6px;
    z-index: 9999;
  }

  .day-norm__icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    background-repeat: no-repeat;
    background-position: center;
    background-size: 16px 16px;
  }

  .day-norm__icon.red-up,
  .day-norm__icon.red-down {
    background-image: url("@/assets/img/diets/norm-red.svg?url");
  }

  .day-norm__icon.yellow-up,
  .day-norm__icon.yellow-down {
    background-image: url("@/assets/img/diets/norm-yellow.svg?url");
  }

  .day-norm__icon.yellow-down,
  .day-norm__icon.red-down {
    transform: rotate(180deg);
  }

  .day-norm__icon.ok {
    width: 15px;
    height: 15px;
    background-image: url("@/assets/img/diets/norm-green.svg?url");
    background-size: 15px 15px;
  }

  .day-norm__title {
    margin-bottom: 8px;
    text-transform: uppercase;
  }

  .day-norm__box {
    display: inline-block;
    width: 3px;
    height: 9px;
  }

  .day-norm__box.day-norm__box--ok {
    background: #80e6ab;
  }

  .day-norm__box.day-norm__box--yellow {
    background: #ffaa39;
  }

  .day-norm__box.day-norm__box--red {
    background: #f56879;
  }

  /* Day link */

  .day-link {
    display: flex;
    align-items: center;
    max-width: calc(100% - 60px);
    height: 32px;
    text-align: center;
    cursor: pointer;
  }

  .day-header.active .day-link a {
    color: var(--color-main-100);
  }

  /* Day menu */
  .day-menu {
    position: absolute;
    top: 8px;
    right: 1px;
    display: none;
    width: 28px;
    height: 28px;
    background: #fff;
  }

  .day-header.active .day-menu {
    display: block;
  }

  .day-menu__items {
    position: absolute;
    top: 28px;
    left: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    background: #fff;
    box-shadow: 0 1px 6px 0 rgb(155 155 155 / 50%);
  }

  .day-menu__items.reversed {
    right: 0;
    left: initial;
  }

  .day-menu__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: left;
    height: 20px;
    padding: 3px 6px;
    font-size: 12px;
    white-space: nowrap;
    cursor: pointer;
  }

  .day-menu__item:hover {
    background: #f8f8f8;
  }

  .day-menu__item.disabled {
    color: var(--color-disabled-text);
    pointer-events: none;
  }

  .day-menu__item.disabled .base-icon {
    color: var(--color-disabled-text);
  }

  .day-menu__item span {
    margin-left: 6px;
  }

  /* Average column */
  .average-header {
    position: relative;
    grid-column: average;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 46px;
  }

  .average-button {
    position: absolute;
    top: 8px;
    left: 1px;
  }

  .average-title {
    display: none;
    font-family: Montserrat-Medium;
    font-size: 12px;
    text-align: center;
    text-transform: uppercase;
  }

  .extended .average-title {
    display: block;
  }

  /* Meal */
  .meal {
    position: relative;
  }

  .meal-name {
    position: absolute;
    top: 0;
    right: 4px;
    left: 4px;
    z-index: 10;
    height: 32px;
    font-family: Montserrat-SemiBold;
    font-size: 14px;
    line-height: 32px;
    text-align: center;
    letter-spacing: 1.4px;
    pointer-events: none;
    background: #f8f8f8;
  }

  .meal-name span {
    position: sticky;
    left: 0;
    display: block;
  }

  /* Energy distribution/hour */
  .energy-distribution-hour {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin: 2px 8px 7px;
    line-height: 18px;
  }

  .day-hour .base-select__selected {
    padding: 1px 12px 1px 2px;
    color: var(--color-tertiary-text);
    border-bottom: 1px solid #fff;
  }

  .day-hour.base-select.is-disabled,
  .day.active .day-hour.base-select.is-disabled {
    pointer-events: none;
    cursor: text;
  }

  .day-hour.base-select.is-disabled .base-select__selected {
    color: var(--color-tertiary-text);
  }

  .day.active .day-hour .base-select__selected {
    color: var(--color-primary-text);
  }

  .day.active .day-hour .base-select__selected:hover {
    border-bottom: 1px solid var(--color-primary-text);
  }

  .day.active .day-hour.base-select.is-disabled .base-select__selected {
    color: var(--color-tertiary-text);
  }

  .day-hour .base-select__selected::after {
    top: 7px;
    right: 2px;
    display: none;
    margin-top: -2px;
  }

  .day-hour .base-select__selected--active::after {
    top: 10px;
  }

  .day.active .day-hour .base-select__selected::after {
    /* display: block; */
  }

  .day.active .day-hour .base-select__selected:hover::after {
    display: block;
  }

  .day.active .day-hour .base-select__selected--active,
  .day.active .day-hour .base-select__selected--active:hover {
    color: var(--color-primary-text);
    border-bottom: 1px solid var(--color-main-100);
  }

  .day.active .day-hour .base-select__selected--active::after {
    display: block;
  }

  .day-hour .base-select__options {
    z-index: 20;
    width: 55px;
  }

  .day-hour .base-select__option {
    padding-right: 5px;
  }

  /* Energy distribution */
  .energy-distribution {
    width: 8px;
    height: 8px;
  }

  .energy-distribution__dot {
    width: 8px;
    height: 8px;
    border-radius: 4px;
  }

  .energy-distribution__dot.green {
    background: var(--color-success-600);
  }

  .energy-distribution__dot.yellow {
    background: var(--color-warning-100);
  }

  .energy-distribution__dot.red {
    background: var(--color-destructive-100);
  }

  .energy-distribution__title {
    margin-bottom: 4px;
  }

  .energy-distribution__data {
    font-family: Montserrat-Medium;
  }

  .energy-distribution__data-target {
    color: var(--color-tertiary-text);
  }

  /* DishOrIngredient */
  .dish-or-ingredient {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 0;
    min-height: 57px;
    padding: 12px 7px 2px 8px;
    margin-bottom: 8px;
    font-size: 14px;
    overflow-wrap: break-word;
    cursor: pointer;
    background: var(--color-main-10);
    border-radius: 4px;
  }

  .dish-or-ingredient.repeated {
    background: var(--color-warning-30);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__copy-button:hover,
  .dish-or-ingredient.repeated .dish-or-ingredient__assignment-button:hover {
    background: var(--color-warning-60);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__copy-button .icon-button,
  .dish-or-ingredient.repeated .dish-or-ingredient__assignment-button .icon-button {
    color: var(--color-warning-100);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__delete-button .icon-button {
    color: var(--color-warning-100);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__copy-button:hover .base-icon {
    color: #e5aa1d;
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__delete-button:hover .base-icon {
    color: #f55f6d;
  }

  .nutrients-bar {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    display: none;
    width: 100%;
    height: 9px;
  }

  .nutrients-bar__content {
    display: flex;
    flex-direction: row;
    align-items: center;
    overflow: hidden;
    font-family: Montserrat-SemiBold;
    font-size: 8px;
    line-height: 9px;
    color: #fff;
    border-radius: 4px 4px 0 0;
  }

  .nutrients-bar span {
    width: 17px;
    max-width: 100%;
    height: 9px;
    text-align: center;
  }

  .nutrients-bar span.hide {
    display: none;
  }

  .nutrients-bar__bar {
    display: flex;
    align-items: center;
    width: calc(100% / 3);
    height: 9px;
    vertical-align: top;
  }

  .nutrients-bar__box {
    display: inline-block;
    width: 3px;
    height: 9px;
    margin-right: 3px;
  }

  .nutrients-bar__protein {
    background: #f16595;
  }

  .nutrients-bar__fat {
    background: #f1c365;
  }

  .nutrients-bar__carbohydrates {
    background: #9f98ff;
  }

  .dish-or-ingredient__footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 22px;
  }

  .dish-or-ingredient__calories {
    font-size: 10px;
    color: var(--color-secondary-text);
  }

  .dish-or-ingredient__actions {
    display: none;
  }

  .day.active .dish-or-ingredient__actions {
    display: flex;
  }

  .day.active .nutrients-bar {
    display: block;
  }

  .add-dish-or-ingredient {
    height: 30px;
  }

  .add-dish-or-ingredient .button {
    display: none;
    width: 100%;
    min-width: 0;
  }

  .day.active .add-dish-or-ingredient .button {
    display: block;
  }

  /* Meal average */
  .meal-average {
    box-sizing: border-box;
    grid-column: average;
    min-width: 0;
    padding: 32px 6px 8px;
    margin-right: 4px;
    overflow: hidden;
    text-align: center;
    background: #f2fafa;
  }

  .meal-average.inactive {
    background: #fff;
  }

  .meal-average__kcal {
    margin-top: 27px;
  }

  .meal-average__percent {
    margin-top: 8px;
  }

  .meal-average__kcal,
  .meal-average__percent {
    font-size: 12px;
  }

  .meal-average__kcal span,
  .meal-average__percent span {
    font-family: Montserrat-SemiBold;
    font-size: 14px;
  }

  .meal-average__kcal span.regular {
    font-family: Montserrat-Regular, sans-serif;
    font-size: 13px;
  }

  .meal-average__kcal .meal-target__kcal,
  .meal-average__percent .meal-target__percent {
    font-family: Montserrat-Regular, sans-serif;
    font-size: 13px;
  }

  .meal-average__nutrients {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .meal-average__nutrient {
    display: flex;
    flex-direction: column;
    width: max-content;
    text-align: center;
  }

  .meal-average__nutrient-name {
    margin-top: 4px;
    font-size: 10px;
  }

  .meal-average__nutrient-value {
    margin-top: 4px;
    font-family: Montserrat-SemiBold;
    font-size: 12px;
  }

  .meal-average__nutrient-percent {
    margin-top: 4px;
    font-size: 10px;
  }

  .doi-name {
    margin-bottom: 13px;
    font-size: 16px;
    color: var(--color-primary-text);
  }

  .doi-description {
    font-size: 12px;
    color: var(--color-primary-text);
  }
</style>
